import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "../components/layouts/Layout";

const SolutionsPage = () => {
  const { category, id } = useParams();
  const [activeTab, setActiveTab] = useState('industry');
  const [roiData, setRoiData] = useState({
    currentCost: '',
    expectedSaving: '',
    implementationCost: ''
  });

  const industrySolutions = [
    {
      id: 1,
      title: "量子リザバソフトウェア（QRC）",
      description: "時系列予測と異常検知に特化した量子コンピューティング技術",
      benefits: ["学習速度 10-50倍高速", "予測精度 95%以上", "ノイズ耐性向上"],
      icon: "⚛️",
      caseStudy: "大手ガス会社で経路最適化により目的関数を14.3%最小化",
      features: ["量子もつれ活用", "リアルタイム学習", "複雑パターン認識"],
      roi: { saving: 40, payback: 8 },
      applications: ["物流最適化", "心電図解析", "核融合制御", "株式予測", "需給バランス"]
    },
    {
      id: 2,
      title: "生成AI建築BIM",
      description: "AIによる建築情報モデルの自動生成と最適化技術",
      benefits: ["作業時間 62.5%削減", "エラー率 80%改善", "設計効率化"],
      icon: "🏗️",
      caseStudy: "大手建材メーカーで年間70万円のコスト削減を実現",
      features: ["2D図面から3D自動生成", "材料コスト自動算出", "リアルタイム設計変更"],
      roi: { saving: 62.5, payback: 6 },
      applications: ["建築設計", "施工計画", "コスト見積", "設計変更影響分析"]
    },
    {
      id: 3,
      title: "カスタムSLM「Hamster」",
      description: "ドメイン特化型の高性能言語モデルソリューション",
      benefits: ["ドメイン特化精度", "多言語対応", "プライバシー保護"],
      icon: "🐹",
      caseStudy: "技術文書自動生成で企業の生産性向上を実現",
      features: ["7B-70B parameters", "128K tokens", "効率的ファインチューニング"],
      roi: { saving: 50, payback: 4 },
      applications: ["技術文書生成", "専門翻訳", "コード生成・レビュー", "契約書解析"]
    },
    {
      id: 4,
      title: "人材マッチングシステム（CorPia）",
      description: "AI駆動の高精度人材マッチングプラットフォーム",
      benefits: ["手数料削減 年間2,450万円", "マッチング精度向上", "満足度 95%以上"],
      icon: "👥",
      caseStudy: "人材紹介企業で年間2,450万円のコスト削減を実現",
      features: ["AIマッチングアルゴリズム", "スキル適合分析", "企業文化マッチング"],
      roi: { saving: 40, payback: 6 },
      applications: ["人材紹介", "採用支援", "転職支援", "スキルマッチング"]
    },
    {
      id: 5,
      title: "農業プラットフォーム",
      description: "農家と消費者を直接つなぐデジタル農業支援システム",
      benefits: ["中間マージン削減", "需要予測最適化", "収益改善"],
      icon: "🌾",
      caseStudy: "長野県農家でのスモールスタート展開予定",
      features: ["直接マッチング", "需要予測AI", "在庫管理システム"],
      roi: { saving: 30, payback: 12 },
      applications: ["農産物販売", "需要予測", "在庫管理", "地域ブランド強化"]
    },
    {
      id: 6,
      title: "つなぎスマイル（地域活性化）",
      description: "地域・コミュニティ活性化のためのマッチングプラットフォーム",
      benefits: ["地域活性化", "最適マッチング", "コミュニティ強化"],
      icon: "🤝",
      caseStudy: "地域コミュニティでの実証実験・導入検討中",
      features: ["AIマッチング", "興味・関心分析", "スキル最適化"],
      roi: { saving: 25, payback: 18 },
      applications: ["地域マッチング", "コミュニティ活性化", "人材活用", "スキル共有"]
    }
  ];

  const challengeSolutions = [
    {
      id: 1,
      title: "時系列予測・異常検知",
      description: "量子リザバコンピューティングによる高精度予測と異常検知",
      icon: "Analytics",
      challenges: ["従来手法の精度限界", "学習時間の長さ", "ノイズ耐性の低さ"],
      solution: "QRCによる量子アルゴリズムで10-50倍高速学習",
      results: ["予測精度 95%以上", "学習時間 1/50", "ノイズ耐性向上"]
    },
    {
      id: 2,
      title: "建築設計業務効率化",
      description: "AIによるBIM自動生成で設計から見積までを一貫して効率化",
      icon: "🏗️",
      challenges: ["手動BIM作業の非効率", "設計変更の工数", "見積作成時間"],
      solution: "2D図面からの3Dモデル自動生成システム",
      results: ["作業時間 62.5%削減", "エラー率 80%改善", "コスト最適化"]
    },
    {
      id: 3,
      title: "人材マッチング最適化",
      description: "AI技術による高精度な人材と企業のマッチング実現",
      icon: "👥",
      challenges: ["マッチング精度の低さ", "高い手数料体系", "属人的判断"],
      solution: "AI駆動マッチングアルゴリズムによる最適化",
      results: ["手数料削減 年間2,450万円", "精度大幅向上", "満足度95%以上"]
    },
    {
      id: 4,
      title: "農業・地域課題解決",
      description: "デジタル技術による農業効率化と地域活性化の両立",
      icon: "🌾",
      challenges: ["中間流通マージン", "後継者不足", "ITリテラシー格差"],
      solution: "直接マッチングプラットフォームとAI需要予測",
      results: ["収益改善", "業務効率化", "地域ブランド強化"]
    }
  ];

  const calculateROI = () => {
    const { currentCost, expectedSaving, implementationCost } = roiData;
    if (!currentCost || !expectedSaving || !implementationCost) return null;

    const annualSaving = (parseFloat(currentCost) * parseFloat(expectedSaving)) / 100;
    const paybackPeriod = parseFloat(implementationCost) / annualSaving;
    const threeYearROI = ((annualSaving * 3 - parseFloat(implementationCost)) / parseFloat(implementationCost)) * 100;

    return {
      annualSaving: annualSaving.toLocaleString(),
      paybackPeriod: paybackPeriod.toFixed(1),
      threeYearROI: threeYearROI.toFixed(1)
    };
  };

  const roiResult = calculateROI();

  return (
    <Layout 
      title="ソリューション一覧" 
      subtitle="業界特化型から課題解決型まで、豊富なAIソリューションをご提供"
    >
      {/* タブナビゲーション */}
      <section style={{ padding: '60px 0 40px', backgroundColor: '#0f172a' }}>
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="text-center">
                <div style={{ display: 'inline-flex', background: 'rgba(30, 41, 59, 0.8)', borderRadius: '8px', padding: '4px' }}>
                  <button
                    className={`btn ${activeTab === 'industry' ? 'btn-custom' : 'btn-outline'}`}
                    onClick={() => setActiveTab('industry')}
                    style={{ 
                      margin: '4px',
                      background: activeTab === 'industry' ? '#3b82f6' : 'transparent',
                      color: activeTab === 'industry' ? 'white' : '#cbd5e1'
                    }}
                  >
                    🏢 業界別ソリューション
                  </button>
                  <button
                    className={`btn ${activeTab === 'challenge' ? 'btn-custom' : 'btn-outline'}`}
                    onClick={() => setActiveTab('challenge')}
                    style={{ 
                      margin: '4px',
                      background: activeTab === 'challenge' ? '#3b82f6' : 'transparent',
                      color: activeTab === 'challenge' ? 'white' : '#cbd5e1'
                    }}
                  >
                    💡 課題別ソリューション
                  </button>
                  <button
                    className={`btn ${activeTab === 'roi' ? 'btn-custom' : 'btn-outline'}`}
                    onClick={() => setActiveTab('roi')}
                    style={{ 
                      margin: '4px',
                      background: activeTab === 'roi' ? '#3b82f6' : 'transparent',
                      color: activeTab === 'roi' ? 'white' : '#cbd5e1'
                    }}
                  >
                    📈 ROI計算ツール
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 業界別ソリューション */}
      {activeTab === 'industry' && (
        <section style={{ padding: '60px 0', backgroundColor: '#1e293b' }}>
          <div className="container">
            <div className="row">
              {industrySolutions.map((solution) => (
                <div key={solution.id} className="col-md-4" style={{ marginBottom: '40px' }}>
                  <div className="premium-card" style={{ 
                    padding: '30px', 
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                      <div style={{ fontSize: '48px', marginBottom: '15px' }}>{solution.icon}</div>
                      <h3 style={{ color: '#3b82f6', marginBottom: '15px' }}>{solution.title}</h3>
                    </div>
                    
                    <p style={{ color: '#cbd5e1', marginBottom: '20px' }}>{solution.description}</p>
                    
                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{ color: '#f1f5f9', marginBottom: '10px' }}>主要効果：</h5>
                      <ul style={{ color: '#94a3b8', marginLeft: '0', listStyle: 'none' }}>
                        {solution.benefits.map((benefit, index) => (
                          <li key={index} style={{ marginBottom: '5px' }}>✅ {benefit}</li>
                        ))}
                      </ul>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{ color: '#f1f5f9', marginBottom: '10px' }}>主要機能：</h5>
                      <ul style={{ color: '#94a3b8', marginLeft: '0', listStyle: 'none' }}>
                        {solution.features.map((feature, index) => (
                          <li key={index} style={{ marginBottom: '5px' }}>• {feature}</li>
                        ))}
                      </ul>
                    </div>

                    {solution.applications && (
                      <div style={{ marginBottom: '20px' }}>
                        <h5 style={{ color: '#f1f5f9', marginBottom: '10px' }}>応用分野：</h5>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                          {solution.applications.map((app, index) => (
                            <span key={index} style={{ 
                              background: 'rgba(16, 185, 129, 0.2)',
                              color: '#10b981',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px'
                            }}>
                              {app}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={{ 
                      background: 'rgba(59, 130, 246, 0.1)', 
                      padding: '15px', 
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <h6 style={{ color: '#3b82f6', marginBottom: '8px' }}>導入事例</h6>
                      <p style={{ color: '#cbd5e1', fontSize: '14px', margin: 0 }}>{solution.caseStudy}</p>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      background: 'rgba(16, 185, 129, 0.1)',
                      padding: '10px',
                      borderRadius: '8px'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#10b981', fontWeight: 'bold' }}>{solution.roi.saving}%</div>
                        <div style={{ color: '#94a3b8', fontSize: '12px' }}>コスト削減</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#10b981', fontWeight: 'bold' }}>{solution.roi.payback}ヶ月</div>
                        <div style={{ color: '#94a3b8', fontSize: '12px' }}>投資回収</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 課題別ソリューション */}
      {activeTab === 'challenge' && (
        <section style={{ padding: '60px 0', backgroundColor: '#1e293b' }}>
          <div className="container">
            <div className="row">
              {challengeSolutions.map((solution) => (
                <div key={solution.id} className="col-md-4" style={{ marginBottom: '40px' }}>
                  <div className="premium-card" style={{ 
                    padding: '30px', 
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                      <div style={{ fontSize: '48px', marginBottom: '15px' }}>{solution.icon}</div>
                      <h3 style={{ color: '#3b82f6', marginBottom: '15px' }}>{solution.title}</h3>
                    </div>
                    
                    <p style={{ color: '#cbd5e1', marginBottom: '25px' }}>{solution.description}</p>
                    
                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{ color: '#f59e0b', marginBottom: '10px' }}>よくある課題：</h5>
                      <ul style={{ color: '#94a3b8', marginLeft: '0', listStyle: 'none' }}>
                        {solution.challenges.map((challenge, index) => (
                          <li key={index} style={{ marginBottom: '5px' }}>❌ {challenge}</li>
                        ))}
                      </ul>
                    </div>

                    <div style={{ 
                      background: 'rgba(59, 130, 246, 0.1)', 
                      padding: '15px', 
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <h6 style={{ color: '#3b82f6', marginBottom: '8px' }}>DeepTeLLソリューション</h6>
                      <p style={{ color: '#cbd5e1', fontSize: '14px', margin: 0 }}>{solution.solution}</p>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{ color: '#10b981', marginBottom: '10px' }}>期待される成果：</h5>
                      <ul style={{ color: '#94a3b8', marginLeft: '0', listStyle: 'none' }}>
                        {solution.results.map((result, index) => (
                          <li key={index} style={{ marginBottom: '5px' }}>🚀 {result}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ROI計算ツール */}
      {activeTab === 'roi' && (
        <section style={{ padding: '60px 0', backgroundColor: '#1e293b' }}>
          <div className="container">
            <div className="row">
              <div className="col-md-8 col-md-offset-2">
                <div className="premium-card" style={{ 
                  padding: '40px',
                  background: 'rgba(30, 41, 59, 0.8)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '12px'
                }}>
                  <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <h2 style={{ color: '#3b82f6', marginBottom: '15px' }}>📈 ROI計算ツール</h2>
                    <p style={{ color: '#cbd5e1' }}>
                      DeepTeLLソリューション導入による投資対効果を簡単に計算できます
                    </p>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                          現在の年間コスト（万円）
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={roiData.currentCost}
                          onChange={(e) => setRoiData({...roiData, currentCost: e.target.value})}
                          placeholder="例: 1000"
                          style={{ 
                            background: 'rgba(30, 41, 59, 0.8)',
                            border: '1px solid #475569',
                            color: '#e2e8f0'
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                          期待コスト削減率（%）
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={roiData.expectedSaving}
                          onChange={(e) => setRoiData({...roiData, expectedSaving: e.target.value})}
                          placeholder="例: 30"
                          style={{ 
                            background: 'rgba(30, 41, 59, 0.8)',
                            border: '1px solid #475569',
                            color: '#e2e8f0'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-12">
                      <div style={{ marginBottom: '30px' }}>
                        <label style={{ color: '#f1f5f9', marginBottom: '8px', display: 'block' }}>
                          導入コスト（万円）
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={roiData.implementationCost}
                          onChange={(e) => setRoiData({...roiData, implementationCost: e.target.value})}
                          placeholder="例: 500"
                          style={{ 
                            background: 'rgba(30, 41, 59, 0.8)',
                            border: '1px solid #475569',
                            color: '#e2e8f0'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {roiResult && (
                    <div style={{ 
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      padding: '25px',
                      borderRadius: '12px',
                      color: 'white'
                    }}>
                      <h4 style={{ marginBottom: '20px', textAlign: 'center' }}>💰 ROI計算結果</h4>
                      <div className="row">
                        <div className="col-md-4">
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                              {roiResult.annualSaving}万円
                            </div>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>年間削減額</div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                              {roiResult.paybackPeriod}年
                            </div>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>投資回収期間</div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                              {roiResult.threeYearROI}%
                            </div>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>3年間ROI</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA セクション */}
      <section style={{ 
        padding: '80px 0', 
        background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 50%, #3b82f6 100%)',
        textAlign: 'center'
      }}>
        <div className="container">
          <div className="row">
            <div className="col-md-8 col-md-offset-2">
              <h2 style={{ color: 'white', marginBottom: '20px' }}>
                あなたの課題に最適なソリューションを提案
              </h2>
              <p style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '30px', fontSize: '18px' }}>
                専門コンサルタントが御社の課題をヒアリングし、最適なAIソリューションをご提案いたします。
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <a href="/contact/demo" className="btn btn-custom btn-lg" style={{ 
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid white',
                  color: 'white'
                }}>
                  🎯 デモを申し込む
                </a>
                <a href="/contact/materials" className="btn btn-custom btn-lg" style={{ 
                  background: 'white',
                  border: '2px solid white',
                  color: '#1e40af'
                }}>
                  📄 資料請求
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SolutionsPage; 